import _merge from 'lodash/merge';
import _findLastIndex from 'lodash/findLastIndex';
import { Lightning } from '@lightningjs/sdk';
import { isToday } from 'date-fns';
import { t } from '../../../../../i18n';
import { Button } from '../../../../button/button.component';
import { NavigationArrow } from '../../../../navigation-arrow/navigationArrow.component';
import { ARROW_LEFT, ARROW_RIGHT } from '../../../../navigation-arrow/navigationArrow.constants';
import { SelectorHeader } from '../../../selectors/selector/selectorHeader/selectorHeader.component';
import { SELECTED_SIGNAL } from '../../../../signals';
import { formatDate } from '../epg.utils';
import { defaultContext, getInitialStyle } from './epgNav.style';
import {
  SIGNAL_GO_LEFT,
  SIGNAL_GO_RIGHT,
  SIGNAL_GO_TO_LIVE,
  SIGNAL_OPEN_DATE_SELECTOR,
  TABS,
} from './epgNav.constants';

export class EpgNav extends Lightning.Component {
  static _template() {
    return _merge(
      {
        LeftContainer: {
          DateSelector: {
            type: Button,
            label: t('epgGrid.today'),
            picto: {
              name: 'Agenda',
            },
          },
          Tabs: {
            type: SelectorHeader,
            signals: {
              [SELECTED_SIGNAL]: '_handleSelectedTab',
            },
            items: [
              { title: t('epgGrid.morning') },
              { title: t('epgGrid.afternoon') },
              { title: t('epgGrid.evening') },
            ],
            selectedIndex: -1,
          },
        },
        ToLive: {
          type: Button,
          label: t('epgGrid.live'),
          picto: {
            name: 'Live',
          },
        },
        Arrows: {
          ArrowLeft: {
            type: NavigationArrow,
            variant: ARROW_LEFT,
          },
          ArrowRight: {
            type: NavigationArrow,
            variant: ARROW_RIGHT,
          },
        },
      },
      getInitialStyle(),
    );
  }

  _build() {
    this._style = defaultContext;
    this._focusable = ['DateSelector', 'Tabs', 'ToLive', 'ArrowLeft', 'ArrowRight'];
    this._focusIndex = 0;
  }

  set date(value) {
    const button = this.tag('DateSelector');

    if (value) {
      const date = new Date(value);
      button.label = isToday(date) ? t('epgGrid.today') : formatDate(date, true);
    } else {
      button.label = t('epgGrid.today');
    }
  }

  set percentage(value) {
    const left = this.tag('ArrowLeft');
    const right = this.tag('ArrowRight');

    left.disabled = value <= 0;
    right.disabled = value >= 1;

    if (left.disabled) {
      if (this._focusIndex === this._focusable.indexOf('ArrowLeft')) {
        this._focusIndex = this._focusable.indexOf('ArrowRight');
        this._waitForKeyUp = true;
      }
    } else if (right.disabled) {
      if (this._focusIndex === this._focusable.indexOf('ArrowRight')) {
        this._focusIndex = this._focusable.indexOf('ArrowLeft');
        this._waitForKeyUp = true;
      }
    }

    this._refocus();
  }

  _handleEnterRelease() {
    this._waitForKeyUp = false;
  }

  _handleEnter() {
    if (this._waitForKeyUp) {
      return;
    }

    switch (this._focusable[this._focusIndex]) {
      case 'DateSelector': {
        this.signal(SIGNAL_OPEN_DATE_SELECTOR);

        break;
      }
      case 'ToLive': {
        this.signal(SIGNAL_GO_TO_LIVE);

        break;
      }
      case 'ArrowLeft': {
        this.signal(SIGNAL_GO_LEFT);

        break;
      }
      case 'ArrowRight': {
        this.signal(SIGNAL_GO_RIGHT);

        break;
      }
      default:
        break;
    }
  }

  _handleLeft() {
    const nextIndex = _findLastIndex(
      this._focusable,
      (value, index) => index < this._focusIndex && !this.tag(value).disabled,
    );

    if (nextIndex < 0) {
      return false;
    }

    this._focusIndex = nextIndex;

    return true;
  }

  _handleRight() {
    const nextIndex = this._focusable.findIndex(
      (value, index) => index > this._focusIndex && !this.tag(value).disabled,
    );

    if (nextIndex < 0) {
      return false;
    }

    this._focusIndex = nextIndex;

    return true;
  }

  _handleSelectedTab(index) {
    this.signal(TABS[index]);
    // According to the design, tabs should not be in a selected state after a click
    this.tag('Tabs').selectedIndex = -1;
  }

  _getFocused() {
    return this.tag(this._focusable[this._focusIndex]);
  }

  _focus() {
    this._waitForKeyUp = false;
  }

  _unfocus() {
    this._waitForKeyUp = false;
  }
}
